import React from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { useTheme, Box, Typography, useMediaQuery } from "@mui/material";
import { toCurrency } from "@helpers";
import { GainLossTypography } from "@rentablo/components";
import { MandatorTypography } from "@rentablo/components";
import { UpdateConnectionButton } from "../UpdateConnectionButton";

const ValuesContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  text-align: end;

  ${({ theme }) => theme.breakpoints.down("lg")} {
    flex-direction: column;
    justify-content: normal;
  }
`;

const SummaryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  flex-grow: 1;
  height: 100%;
`;

export interface AccountRowSummaryProps {
  amount: number;
  currency: string;
  sinceYear?: number;
  changeAbs: number;
  showNotification?: boolean;
  onClickUpdate?: (event: React.MouseEvent<HTMLElement>) => void;
  nativePortfolioValue?: number;
  nativeCurrency?: string;
}

export const AccountRowSummary = ({
  amount,
  currency,
  sinceYear,
  changeAbs,
  showNotification = false,
  onClickUpdate,
  nativePortfolioValue,
  nativeCurrency,
}: AccountRowSummaryProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <SummaryWrapper>
      <ValuesContainer>
        <MandatorTypography variant={isSm ? "body2" : "subtitle2"}>
          {toCurrency(amount, currency, true)}
        </MandatorTypography>

        {nativePortfolioValue !== undefined && nativeCurrency && (
          <Typography
            variant="body4"
            color={theme.palette.text.secondary}
            noWrap
          >
            ({toCurrency(nativePortfolioValue, nativeCurrency, true)})
          </Typography>
        )}

        {Boolean(sinceYear && changeAbs && !isSm) && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            gap={1}
          >
            <Typography
              variant="body4"
              color={theme.palette.text.secondary}
              noWrap
            >
              {t("depotPage.since")} {sinceYear}:
            </Typography>
            <GainLossTypography variant="body4" value={changeAbs}>
              {toCurrency(changeAbs, currency, true)}
            </GainLossTypography>
          </Box>
        )}

        {showNotification && onClickUpdate && (
          <UpdateConnectionButton onClick={onClickUpdate} />
        )}
      </ValuesContainer>
    </SummaryWrapper>
  );
};
